// src/layout/Sidebar.jsx
//
// Collapsible app-shell sidebar for the redesigned Jade Orion header/nav.
// Renders NAV_SECTIONS (pages/navConfig.jsx) split into two labeled blocks
// by each group's `section` field ("lifecycle" | "platform"), matching the
// approved design. Routing/permission logic is unchanged from the old
// inline Dashboard.jsx sidebar: `ctx.show`/`ctx.anyVisible` still gate
// entries, `to`/`end` still drive real react-router NavLinks, autoOpenPaths
// still auto-expands the group containing the current route (computed by
// the caller and passed in as `openMap`/`onToggleGroup`).
//
// Submenu entries can themselves be nested groups (e.g. "rag" -> "rag_cloud"
// / "rag_onprem"), so SubEntry below is recursive rather than assuming
// every submenu row is a leaf link.
//
// Usage: <Sidebar collapsed={collapsed} openMap={openMap} onToggleGroup={fn}
//                  ctx={ctx} user={{ username, role }} />

import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_SECTIONS } from "../pages/navConfig";
import companyLogo from "../assets/jade-logo.png";

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Whether the current route falls inside this entry (leaf `to`, autoOpenPaths, or any nested item) — drives the "has-active" accent on a closed group's own row. */
function pathMatches(entry, pathname) {
  if (entry.autoOpenPaths) return entry.autoOpenPaths.some((p) => pathname.startsWith(p));
  if (entry.to) return entry.end ? pathname === entry.to : pathname.startsWith(entry.to);
  if (entry.items) return entry.items.some((it) => pathMatches(it, pathname));
  return false;
}

/** Recursive submenu row — a leaf link, a disabled "soon" row, or (e.g. RAG Cloud/On-Prem) a nested collapsible group. */
function SubEntry({ entry, ctx, openMap, onToggleGroup, pathname }) {
  if (entry.visible && !entry.visible(ctx)) return null;

  if (entry.type === "group") {
    const open = !!openMap[entry.key];
    const items = (entry.items || []).filter((it) => !it.visible || it.visible(ctx));
    if (items.length === 0) return null;
    const hasActive = pathMatches(entry, pathname);

    return (
      <div className={`orion-navgroup nested${open ? " open" : ""}`}>
        <button
          type="button"
          className={`orion-subrow group-row${hasActive ? " has-active" : ""}`}
          onClick={() => onToggleGroup(entry.key)}
        >
          <span className="orion-subdot" />
          <span className="orion-subname">{entry.label}</span>
          <span className="chev nested-chev"><ChevronDown size={13} /></span>
        </button>
        <div className="orion-subwrap nested">
          <div style={{ padding: "2px 0 4px 0" }}>
            {items.map((it) => (
              <SubEntry key={it.key} entry={it} ctx={ctx} openMap={openMap} onToggleGroup={onToggleGroup} pathname={pathname} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (entry.disabled) {
    return (
      <span className="orion-subrow disabled" title="Coming soon">
        <span className="orion-subdot" />
        <span className="orion-subname">{entry.label}</span>
        <span className="orion-soon-tag">Soon</span>
      </span>
    );
  }

  return (
    <NavLink
      to={entry.to}
      end={entry.end}
      className={({ isActive }) => `orion-subrow${isActive ? " active" : ""}`}
    >
      <span className="orion-subdot" />
      <span className="orion-subname">{entry.label}</span>
    </NavLink>
  );
}
SubEntry.propTypes = {
  entry: PropTypes.shape({
    key: PropTypes.string,
    type: PropTypes.string,
    to: PropTypes.string,
    end: PropTypes.bool,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    visible: PropTypes.func,
    items: PropTypes.array,
    autoOpenPaths: PropTypes.array,
  }).isRequired,
  ctx: PropTypes.object.isRequired,
  openMap: PropTypes.object.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
};

function NavGroup({ entry, ctx, openMap, onToggleGroup, pathname, tone }) {
  if (entry.visible && !entry.visible(ctx)) return null;
  const Icon = entry.icon;
  const open = !!openMap[entry.key];
  const items = (entry.items || []).filter((it) => !it.visible || it.visible(ctx));
  if (items.length === 0) return null;
  const hasActive = pathMatches(entry, pathname);

  return (
    <div className={`orion-navgroup${open ? " open" : ""}`}>
      <button
        type="button"
        className={`orion-nrow${hasActive ? " has-active" : ""}`}
        onClick={() => onToggleGroup(entry.key)}
      >
        <span className={`ico-chip tone-${tone}`}><Icon size={15} /></span>
        <span className="lbl">{entry.label}</span>
        <span className="chev"><ChevronDown size={15} /></span>
      </button>
      <div className="orion-subwrap">
        <div style={{ padding: "3px 0 6px 0" }}>
          {items.map((it) => (
            <SubEntry key={it.key} entry={it} ctx={ctx} openMap={openMap} onToggleGroup={onToggleGroup} pathname={pathname} />
          ))}
        </div>
      </div>
    </div>
  );
}
NavGroup.propTypes = {
  entry: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    visible: PropTypes.func,
    items: PropTypes.array,
    autoOpenPaths: PropTypes.array,
  }).isRequired,
  ctx: PropTypes.object.isRequired,
  openMap: PropTypes.object.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["lifecycle", "platform"]).isRequired,
};

export default function Sidebar({ collapsed, openMap, onToggleGroup, ctx, user, dashActive = false, onDashClick }) {
  const location = useLocation();
  const pathname = location.pathname || "/";

  const lifecycleGroups = NAV_SECTIONS.filter((e) => e.section === "lifecycle");
  const platformGroups = NAV_SECTIONS.filter((e) => e.section === "platform");
  // Bare `type: "item"` entries other than dashboard (e.g. "faq") — pinned
  // below the two grouped sections, same as they used to sit at the bottom
  // of the flat nav list.
  const trailingItems = NAV_SECTIONS.filter((e) => e.type === "item" && e.key !== "dashboard");
  const dashboardEntry = NAV_SECTIONS.find((e) => e.key === "dashboard");
  const DashIcon = dashboardEntry?.icon;

  return (
    <aside className={`orion-sidebar${collapsed ? " collapsed" : ""}`}>
      <div className="orion-sidebar-inner">
        <div className="orion-brand-row">
          <img src={companyLogo} alt="Jade Global" className="orion-brand-logo" />
          <div className="orion-brand-text">
            <div className="t1">Jade Orion</div>
            <div className="t2">Enterprise AI</div>
          </div>
        </div>

        <nav className="orion-navscroll">
          {dashboardEntry && (
            <NavLink
              to={dashboardEntry.to}
              end={dashboardEntry.end}
              onClick={onDashClick}
              className={({ isActive }) => `orion-dash-row${isActive || dashActive ? " active" : ""}`}
            >
              <span className="ico-chip tone-neutral"><DashIcon size={15} /></span>
              <span className="lbl">{dashboardEntry.label}</span>
            </NavLink>
          )}

          {lifecycleGroups.length > 0 && <div className="orion-sec-label">Delivery Lifecycle</div>}
          {lifecycleGroups.map((entry) => (
            <NavGroup key={entry.key} entry={entry} ctx={ctx} openMap={openMap} onToggleGroup={onToggleGroup} pathname={pathname} tone="lifecycle" />
          ))}

          {platformGroups.length > 0 && <div className="orion-sec-label gold">Platform</div>}
          {platformGroups.map((entry) => (
            <NavGroup key={entry.key} entry={entry} ctx={ctx} openMap={openMap} onToggleGroup={onToggleGroup} pathname={pathname} tone="platform" />
          ))}

          {trailingItems.map((entry) => {
            if (entry.visible && !entry.visible(ctx)) return null;
            const ItemIcon = entry.icon;
            return (
              <NavLink
                key={entry.key}
                to={entry.to}
                end={entry.end}
                className={({ isActive }) => `orion-nrow${isActive ? " active" : ""}`}
                style={{ marginTop: "4px" }}
              >
                <span className="ico-chip tone-neutral"><ItemIcon size={15} /></span>
                <span className="lbl">{entry.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="orion-user-footer">
          <div className="orion-user-footer-inner">
            <div className="orion-avatar-sm">{initialsOf(user.username)}</div>
            <div className="orion-brand-text">
              <div className="name">{user.username}</div>
              <div className="role">{user.role || "—"}</div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  openMap: PropTypes.object.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  ctx: PropTypes.object.isRequired,
  user: PropTypes.shape({ username: PropTypes.string, role: PropTypes.string }).isRequired,
  dashActive: PropTypes.bool,
  onDashClick: PropTypes.func,
};
